import type { Thread } from "./types";

export interface WorktreeThreadReference {
  readonly id: Thread["id"];
  readonly worktreePath?: string | null;
}

function normalizeWorktreePath(path: string | null | undefined): string | null {
  const trimmed = path?.trim();
  if (!trimmed) {
    return null;
  }
  return trimmed;
}

function appendComparablePathSegment(segments: string[], segment: string, absolute: boolean): void {
  if (segment.length === 0 || segment === ".") return;
  if (segment !== "..") {
    segments.push(segment);
    return;
  }

  const previous = segments.at(-1);
  if (previous !== undefined && previous !== "..") {
    segments.pop();
    return;
  }
  if (!absolute) segments.push(segment);
}

function collapseComparablePathSegments(path: string, absolute: boolean): string {
  const segments: string[] = [];
  for (const segment of path.split("/")) {
    appendComparablePathSegment(segments, segment, absolute);
  }
  return segments.join("/");
}

function worktreePathComparisonKey(path: string | null | undefined): string | null {
  const normalized = normalizeWorktreePath(path)?.replace(/\\/g, "/");
  if (!normalized) return null;

  const absolute = normalized.startsWith("/");
  const comparable = `${absolute ? "/" : ""}${collapseComparablePathSegments(normalized, absolute)}`;
  // A false shared-path match blocks cleanup, which is safe. Missing a match
  // on a case-insensitive checkout can delete a worktree another thread uses.
  return comparable.toLowerCase();
}

export function getOrphanedWorktreePathForThread(
  threads: readonly WorktreeThreadReference[],
  threadId: Thread["id"],
): string | null {
  const targetThread = threads.find((thread) => thread.id === threadId);
  if (!targetThread) {
    return null;
  }

  const targetWorktreePath = normalizeWorktreePath(targetThread.worktreePath);
  if (!targetWorktreePath) {
    return null;
  }
  const targetComparisonKey = worktreePathComparisonKey(targetWorktreePath);

  const isShared = threads.some((thread) => {
    if (thread.id === threadId) {
      return false;
    }
    return worktreePathComparisonKey(thread.worktreePath) === targetComparisonKey;
  });

  return isShared ? null : targetWorktreePath;
}

export function formatWorktreePathForDisplay(worktreePath: string): string {
  const trimmed = worktreePath.trim();
  if (!trimmed) {
    return worktreePath;
  }

  const normalized = trimmed.replace(/\\/g, "/").replace(/\/+$/, "");
  const parts = normalized.split("/");
  const lastPart = parts[parts.length - 1]?.trim() ?? "";
  return lastPart.length > 0 ? lastPart : trimmed;
}
